import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  TextField,
} from '@mui/material';
import { BillingSubscription } from '../../../../types/admin';
import { format } from 'date-fns';
import { formatCurrency } from '../../../../utils/formatters';

interface SubscriptionDetailModalProps {
  open: boolean;
  subscription: BillingSubscription;
  onClose: () => void;
  onCancelSubscription?: (subscriptionId: string, reason?: string) => Promise<{ success: boolean }>;
}

export default function SubscriptionDetailModal({
  open,
  subscription,
  onClose,
  onCancelSubscription,
}: SubscriptionDetailModalProps) {
  const [canceling, setCanceling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const handleCancel = async () => {
    if (onCancelSubscription) {
      setCanceling(true);
      try {
        await onCancelSubscription(subscription.id, cancelReason);
        onClose();
      } finally {
        setCanceling(false);
        setCancelReason('');
      }
    }
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Subscription Details
        <Typography variant="body2" color="text.secondary">
          {subscription.paddle_subscription_id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Customer Information
          </Typography>
          <DetailRow label="Name" value={subscription.user_name} />
          <DetailRow label="Email" value={subscription.user_email} />
          <DetailRow label="User ID" value={subscription.user_id} />
          <DetailRow label="Paddle Customer ID" value={subscription.paddle_customer_id} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Subscription Details
          </Typography>
          <DetailRow
            label="Tier"
            value={<Chip label={subscription.tier.toUpperCase()} size="small" color="primary" />}
          />
          <DetailRow
            label="Status"
            value={<Chip label={subscription.status.toUpperCase()} size="small" />}
          />
          <DetailRow
            label="Amount"
            value={`${formatCurrency(subscription.amount)} / ${subscription.interval}`}
          />
          <DetailRow label="Currency" value={subscription.currency} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Payment Information
          </Typography>
          <DetailRow
            label="Payment Method"
            value={
              <Box>
                <Box sx={{ textTransform: 'capitalize' }}>
                  {subscription.payment_method_brand || subscription.payment_method}
                </Box>
                {subscription.payment_method_last4 && (
                  <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    ••••{subscription.payment_method_last4}
                  </Box>
                )}
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Billing Period
          </Typography>
          <DetailRow
            label="Current Period Start"
            value={format(new Date(subscription.current_period_start), 'PPP')}
          />
          <DetailRow
            label="Current Period End"
            value={format(new Date(subscription.current_period_end), 'PPP')}
          />
          {subscription.trial_end && (
            <DetailRow label="Trial End" value={format(new Date(subscription.trial_end), 'PPP')} />
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Dates
          </Typography>
          <DetailRow label="Created" value={format(new Date(subscription.created_at), 'PPP p')} />
          <DetailRow label="Last Updated" value={format(new Date(subscription.updated_at), 'PPP p')} />
        </Box>

        {subscription.status !== 'canceled' && subscription.status !== 'expired' && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Cancel Subscription
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Cancellation Reason (optional)"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {subscription.status !== 'canceled' && subscription.status !== 'expired' && (
          <Button onClick={handleCancel} color="error" variant="contained" disabled={canceling}>
            {canceling ? 'Canceling...' : 'Cancel Subscription'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
